import { yupResolver } from '@hookform/resolvers/yup'
import Button from 'components/common/Button'
import * as Common from 'components/common/Common'
import * as Typography from 'components/common/TypographyKiosk'
import InputCheckbox from 'components/inputs/InputCheckbox'
import InputDateRange from 'components/inputs/InputDateRange'
import InputPersonCount from 'components/inputs/InputPersonCount'
import { ROUTE_MAKE_RESERVATION_ROOMS_FOUND } from 'constants/routes'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { selectFitCustomResolution, selectIsKiosk } from 'redux/slices/app'
import { selectRoomSearchParams, setSearchParams } from 'redux/slices/room'
import { IFindRoomForm, countDaysBetweenDates, findRoomSchema } from 'shared-ui'
import styled, { css } from 'styled-components'

export type TFormSearchRoomChosenInput = 'date' | 'rooms' | null

interface IFormSearchRoomProps {
  withCheckbox?: boolean
}

const StretchedForm = styled.form(
  () => css`
    width: 100%;
  `
)

const FormSearchRoom = ({ withCheckbox = false }: IFormSearchRoomProps) => {
  const isKiosk = useAppSelector(selectIsKiosk)
  const navigate = useNavigate()
  const searchData = useAppSelector(selectRoomSearchParams)
  const isMobile = useAppSelector(state =>
    selectFitCustomResolution(state, 1266)
  )
  const { t } = useTranslation()

  const formFindRoom = useForm<IFindRoomForm>({
    defaultValues: {
      adults: searchData.adults || 1,
      children: searchData.children || 0,
      rooms: searchData.rooms || 1,
      stayDate: {
        startDate: searchData.stayDate?.startDate
          ? new Date(searchData.stayDate.startDate).toISOString().split('T')[0]
          : '',
        endDate: searchData.stayDate?.endDate
          ? new Date(searchData.stayDate.endDate).toISOString().split('T')[0]
          : '',
      },
    },
    resolver: yupResolver(findRoomSchema(t)),
  })

  const [selectedInput, setSelectedInput] =
    useState<TFormSearchRoomChosenInput>(null)

  const dispatch = useAppDispatch()

  const handleSearch = () => {
    const { adults, children, rooms, stayDate } = formFindRoom.watch()

    const startDate = new Date(stayDate.startDate)
    const endDate = new Date(stayDate.endDate)

    const nights = countDaysBetweenDates(startDate, endDate)

    dispatch(
      setSearchParams({
        adults,
        children,
        rooms,
        stayDate: {
          startDate: stayDate.startDate,
          endDate: stayDate.endDate,
          nights,
        },
      })
    )

    navigate(ROUTE_MAKE_RESERVATION_ROOMS_FOUND)
  }

  const renderSearchButton = () => (
    <Button type="submit">{t('findTheRoom.button')}</Button>
  )

  useEffect(() => {
    if (searchData.stayDate.startDate && searchData.stayDate.endDate) {
      formFindRoom.setValue('stayDate', {
        startDate: new Date(searchData.stayDate.startDate)
          .toISOString()
          .split('T')[0],
        endDate: new Date(searchData.stayDate.endDate)
          .toISOString()
          .split('T')[0],
      })
    }
  }, [searchData, formFindRoom])

  return (
    <Common.Div
      flex="row"
      w="100%"
    >
      <StretchedForm onSubmit={formFindRoom.handleSubmit(handleSearch)}>
        <Common.Div flex="column">
          <Typography.Header
            mb={3}
            s={32}
            align="center"
            secondary
          >
            {t('findTheRoom.desc')}
          </Typography.Header>
          <Common.Div
            flex={isKiosk || !isMobile ? 'row' : 'column'}
            w="100%"
            justifyContent="center"
            mb={withCheckbox ? 2 : 0}
            gap="16px"
          >
            <Common.Div
              flex={isKiosk || !isMobile ? 'row' : 'column'}
              gap="8px"
              position="relative"
            >
              <InputDateRange
                form={formFindRoom}
                selectedInput={selectedInput}
                setSelectedInput={setSelectedInput}
              />
              <InputPersonCount
                form={formFindRoom}
                selectedInput={selectedInput}
                setSelectedInput={setSelectedInput}
              />
            </Common.Div>
            {renderSearchButton()}
          </Common.Div>
          {withCheckbox && (
            <Common.Div flex="row">
              <InputCheckbox
                name="breakfast"
                form={formFindRoom}
                label={t('reservationDetails.form.breakfastIncluded')}
                secondaryLabel=" - 50 zl"
              />
            </Common.Div>
          )}
        </Common.Div>
      </StretchedForm>
    </Common.Div>
  )
}

export default FormSearchRoom
